import { useEffect, useRef, useState } from "react";
import { Sheet, View, Button, YStack } from "tamagui";
import useHomePageSheetStore from "@utils/store/useHomePageSheetStore";
import TextCustom, { TextSize } from "@components/TextCoustom";
import * as styles from "@screen/Register/style";
import { IoMdClose } from "react-icons/io";
import SheetHeader from "@screen/Register/components/SheetHeader";
import * as basicStyle from "@styles/index";
import { zIndexes } from "@utils/zIndexLevel";

const ShareSheet: React.FC = () => {
  const { isSheetOpen, sheetContent, setIsOpen, closeSheet } = useHomePageSheetStore();
  const fit = sheetContent?.fitContent;

  // fitContent：量內容實際像素高，用 constant 模式讓 sheet 高度「貼齊到按鈕」，不留空白也不滾動。
  //（Tamagui 的 snapPointsMode="fit" 在 web 量測不準會切到內容，故改自行量測。）
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [fitHeight, setFitHeight] = useState(0);

  useEffect(() => {
    if (!fit || !isSheetOpen) {
      setFitHeight(0);
      return;
    }
    const measure = () => {
      const el = contentRef.current;
      if (!el || !el.offsetHeight) return;
      const cap = typeof window !== "undefined" ? Math.round(window.innerHeight * 0.92) : el.offsetHeight;
      setFitHeight(Math.min(el.offsetHeight, cap));
    };
    // 開啟動畫 / 字體 settle 後各量一次
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 260);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [fit, isSheetOpen, sheetContent]);

  const useConstant = !!fit && fitHeight > 0;

  return (
    <>
      <Sheet
        disableDrag
        open={isSheetOpen}
        onOpenChange={setIsOpen}
        position={0}
        animation="bouncy"
        snapPoints={useConstant ? [fitHeight] : fit ? [80] : [40, 10]}
        modal={true}
        snapPointsMode={useConstant ? "constant" : "percent"}
        zIndex={99999999999999}
        dismissOnSnapToBottom>
        <Sheet.Overlay
          animation="bouncy"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />
        <Sheet.Frame overflow={fit ? "hidden" : "scroll"} style={{ scrollbarWidth: "none" }} {...styles.shadow}>
          {sheetContent && (
            <div ref={contentRef}>
              <YStack pb={fit ? "$6" : "$0"}>
                <SheetHeader
                  other={{ py: "$5", pt: "$7" }}
                  title={sheetContent.header}
                  right={{
                    icon: IoMdClose,
                    func: () => setIsOpen(false),
                  }}
                />
                <YStack gap={"$5"} px={"$5"} justifyContent="space-between">
                  {sheetContent.contentArray.map((item, index) => (
                    <YStack key={index} alignItems="flex-start">
                      <TextCustom str={item.title} type={TextSize.NORMAL} bold />
                      <TextCustom str={item.content} type={TextSize.SMALL} align="left" />
                    </YStack>
                  ))}
                  {sheetContent.button && (
                    <Button mt={"$4"} {...basicStyle.ShareBlueButton} onPress={closeSheet} fontSize={TextSize.SUBSMALL}>
                      {sheetContent.button.buttonText}
                    </Button>
                  )}
                </YStack>
              </YStack>
            </div>
          )}
        </Sheet.Frame>
      </Sheet>
    </>
  );
};

export default ShareSheet;
